import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponseModel } from '../../@types/base';
import { CONSTS } from './const';
import { dispatch } from '../store';
import axios from '../../utils/axios';
import { HttpStatusCodes } from '../../constants/http-status-codes.enum';
import {
  AlarmAndWarningCategoryDistributionModel,
  AlarmAndWarningCountModel,
  AlarmAndWarningListModel,
  DailyAlarmsAndWarningsCountModel
} from '../../@types/alarm-and-warning';
import { setEndDate } from './localSettings';

// ----------------------------------------------------------------------

type CameraState = {
  alarmAndWarningListError: boolean;
  isAlarmAndWarningListLoading: boolean;
  alarmAndWarningList: AlarmAndWarningListModel[];

  createAlarmAndWarningListModalIsOpen: boolean;
  updateAlarmAndWarningListModalIsOpen: boolean;

  alarmAndWarningCountError: boolean;
  isAlarmAndWarningCountLoading: boolean;
  alarmAndWarningCount: AlarmAndWarningCountModel;

  createAlarmAndWarningCountModalIsOpen: boolean;
  updateAlarmAndWarningCountModalIsOpen: boolean;

  dailyAlarmsAndWarningsCountError: boolean;
  isDailyAlarmsAndWarningsCountLoading: boolean;
  dailyAlarmsAndWarningsCount: DailyAlarmsAndWarningsCountModel[];

  createDailyAlarmsAndWarningsCountModalIsOpen: boolean;
  updateDailyAlarmsAndWarningsCountModalIsOpen: boolean;

  alarmAndWarningCategoryDistributionError: boolean;
  isAlarmAndWarningCategoryDistributionLoading: boolean;
  alarmAndWarningCategoryDistribution: AlarmAndWarningCategoryDistributionModel[];

  createAlarmAndWarningCategoryDistributionModalIsOpen: boolean;
  updateAlarmAndWarningCategoryDistributionModalIsOpen: boolean;
};

const alarmAndWarningCountInit: AlarmAndWarningCountModel = {
  alarmCount: 0,
  warningCount: 0
};


const initialState: CameraState = {
  alarmAndWarningListError: false,
  isAlarmAndWarningListLoading: false,
  alarmAndWarningList: [],

  createAlarmAndWarningListModalIsOpen: false,
  updateAlarmAndWarningListModalIsOpen: false,

  alarmAndWarningCountError: false,
  isAlarmAndWarningCountLoading: false,
  alarmAndWarningCount: alarmAndWarningCountInit,

  dailyAlarmsAndWarningsCountError: false,
  isDailyAlarmsAndWarningsCountLoading: false,
  dailyAlarmsAndWarningsCount: [],

  createDailyAlarmsAndWarningsCountModalIsOpen: false,
  updateDailyAlarmsAndWarningsCountModalIsOpen: false,

  createAlarmAndWarningCountModalIsOpen: false,
  updateAlarmAndWarningCountModalIsOpen: false,

  alarmAndWarningCategoryDistributionError: false,
  isAlarmAndWarningCategoryDistributionLoading: false,
  alarmAndWarningCategoryDistribution: [],

  createAlarmAndWarningCategoryDistributionModalIsOpen: false,
  updateAlarmAndWarningCategoryDistributionModalIsOpen: false
};

const slice = createSlice({
  name: 'alarmandwarning',
  initialState,
  reducers: {
    // ----------------------------------------------------------------------
    // ALERT AND WARNING LIST
    // ----------------------------------------------------------------------

    // START ALERT AND WARNING LIST LOADING
    startAlarmAndWarningListLoading(state) {
      state.isAlarmAndWarningListLoading = true;
    },

    stopAlarmAndWarningListLoading(state) {
      state.isAlarmAndWarningListLoading = false;
    },

    // HAS ERROR ALERT AND WARNING LIST
    hasErrorAlarmAndWarningList(state, action) {
      state.isAlarmAndWarningListLoading = false;
      state.alarmAndWarningListError = action.payload;
    },

    // GET ALERT AND WARNING LIST
    getAllAlarmAndWarningListSuccess(state, action) {
      state.isAlarmAndWarningListLoading = false;
      state.alarmAndWarningList = action.payload;
    },

    // ----------------------------------------------------------------------
    // ALERT AND WARNING COUNT
    // ----------------------------------------------------------------------

    // START ALERT AND WARNING COUNT LOADING
    startAlarmAndWarningCountLoading(state) {
      state.isAlarmAndWarningCountLoading = true;
    },

    // HAS ERROR ALERT AND WARNING COUNT
    hasErrorAlarmAndWarningCount(state, action) {
      state.isAlarmAndWarningCountLoading = false;
      state.alarmAndWarningCountError = action.payload;
    },

    // GET ALERT AND WARNING COUNT
    getAllAlarmAndWarningCountSuccess(state, action) {
      state.isAlarmAndWarningCountLoading = false;
      state.alarmAndWarningCount = action.payload;
    },

    // ----------------------------------------------------------------------
    // DAILY ALARMS AND WARNINGS COUNT
    // ----------------------------------------------------------------------

    // START DAILY ALARMS AND WARNINGS COUNT LOADING
    startDailyAlarmsAndWarningsCountLoading(state) {
      state.isDailyAlarmsAndWarningsCountLoading = true;
    },

    // HAS ERROR DAILY ALARMS AND WARNINGS COUNT
    hasErrorDailyAlarmsAndWarningsCount(state, action) {
      state.isDailyAlarmsAndWarningsCountLoading = false;
      state.dailyAlarmsAndWarningsCountError = action.payload;
    },

    // GET DAILY ALARMS AND WARNINGS COUNT
    getDailyAlarmsAndWarningsCountSuccess(state, action) {
      state.isDailyAlarmsAndWarningsCountLoading = false;
      state.dailyAlarmsAndWarningsCount = action.payload;
    },

    // ----------------------------------------------------------------------
    // ALERT AND WARNING CATEGORY DISTRIBUTION
    // ----------------------------------------------------------------------

    // START ALERT AND WARNING CATEGORY DISTRIBUTION LOADING
    startAlarmAndWarningCategoryDistributionLoading(state) {
      state.isAlarmAndWarningCategoryDistributionLoading = true;
    },

    // HAS ERROR ALERT AND WARNING CATEGORY DISTRIBUTION
    hasErrorAlarmAndWarningCategoryDistribution(state, action) {
      state.isAlarmAndWarningCategoryDistributionLoading = false;
      state.alarmAndWarningCategoryDistributionError = action.payload;
    },

    // GET ALERT AND WARNING CATEGORY DISTRIBUTION
    getAllAlarmAndWarningCategoryDistributionSuccess(state, action) {
      state.isAlarmAndWarningCategoryDistributionLoading = false;
      state.alarmAndWarningCategoryDistribution = action.payload;
    }
  }
});

// Reducer
export default slice.reducer;

// Actions
export const {} = slice.actions;

// ----------------------------------------------------------------------
// ALERT AND WARNING

export async function setScanSuccess(eventId: number, isSuccessful: boolean) {
  dispatch(slice.actions.startAlarmAndWarningListLoading());
  try {
    await axios
      .post(CONSTS.EVENT_SET_SCAN_SUCCESS, {
          eventId,
          isSuccessful
        }
      )
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    // const { startDate, endDate } = useSelector((state: RootState) => state.localSettings);
    // getAllAlarmAndWarningListBetweenDatesWithReturn(startDate, endDate)
    setEndDate(new Date().toISOString());
    dispatch(slice.actions.stopAlarmAndWarningListLoading());
    return null;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorAlarmAndWarningList(error));
    return error.response.data;
  }
}

export async function getAllAlarmAndWarningListBetweenDatesWithReturn(
  startDate: string,
  endDate: string,
  locationId: string = '',
  cameraId: string = '',
  limit?: number,
  offset?: number
) {
  dispatch(slice.actions.startAlarmAndWarningListLoading());
  try {
    if (startDate.includes('T')) {
      startDate = startDate.slice(0, startDate.indexOf('T'));
    }
    if (endDate.includes('T')) {
      endDate = endDate.slice(0, endDate.indexOf('T'));
    }
    let requestURL = `${CONSTS.ALERT_GET_GET_FILTER_EVENTS_BY_ALERT_TYPE_AND_DATE_ALARM_AND_WARNING_LIST}?startDate=${startDate}&endDate=${endDate}`;
    if (locationId) {
      requestURL += `&locationId=${locationId}`;
    }
    if (cameraId) {
      requestURL += `&cameraId=${cameraId}`;
    }
    if (limit) {
      requestURL += `&limit=${limit}`;
    }
    if (offset) {
      requestURL += `&offset=${offset}`;
    }
    const response: AxiosResponseModel<AlarmAndWarningListModel> = await axios
      .get(requestURL)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getAllAlarmAndWarningListSuccess(response.data?.data)
      );
    } else {
      dispatch(slice.actions.getAllAlarmAndWarningListSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorAlarmAndWarningList(error));
    return error.response.data;
  }
}

export async function getAllAlarmAndWarningCountBetweenDatesWithReturn(
  startDate: string,
  endDate: string,
  locationId: string = '',
  cameraId: string = ''
) {
  dispatch(slice.actions.startAlarmAndWarningCountLoading());
  try {
    if (startDate.includes('T')) {
      startDate = startDate.slice(0, startDate.indexOf('T'));
    }
    if (endDate.includes('T')) {
      endDate = endDate.slice(0, endDate.indexOf('T'));
    }
    let requestURL = `${CONSTS.DASHBOARD_GET_GET_FILTER_EVENTS_BY_ALERT_TYPE_AND_DATE_ALARM_AND_WARNING_COUNT}?startDate=${startDate}&endDate=${endDate}`;
    if (locationId) {
      requestURL += `&locationId=${locationId}`;
    }
    if (cameraId) {
      requestURL += `&cameraId=${cameraId}`;
    }
    const response: AxiosResponseModel<AlarmAndWarningCountModel> = await axios
      .get(requestURL)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getAllAlarmAndWarningCountSuccess(response.data?.data)
      );
    } else {
      dispatch(
        slice.actions.getAllAlarmAndWarningCountSuccess(
          alarmAndWarningCountInit
        )
      );
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorAlarmAndWarningCount(error));
    return error.response.data;
  }
}

export async function getDailyAlarmsAndWarningsCountBetweenDatesWithReturn(
  startDate: string,
  endDate: string,
  locationId: string = '',
  cameraId: string = ''
) {
  dispatch(slice.actions.startDailyAlarmsAndWarningsCountLoading());
  try {
    if (startDate.includes('T')) {
      startDate = startDate.slice(0, startDate.indexOf('T'));
    }
    if (endDate.includes('T')) {
      endDate = endDate.slice(0, endDate.indexOf('T'));
    }
    let requestURL = `${CONSTS.ALERT_GET_GET_DAILY_ALARMS_AND_WARNINGS_COUNT_BY_DATES}?startDate=${startDate}&endDate=${endDate}`;
    if (locationId) {
      requestURL += `&locationId=${locationId}`;
    }
    if (cameraId) {
      requestURL += `&cameraId=${cameraId}`;
    }
    const response: AxiosResponseModel<DailyAlarmsAndWarningsCountModel> = await axios
      .get(requestURL)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getDailyAlarmsAndWarningsCountSuccess(response.data?.data)
      );
    } else {
      dispatch(slice.actions.getDailyAlarmsAndWarningsCountSuccess([]));
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorDailyAlarmsAndWarningsCount(error));
    return error.response.data;
  }
}

export async function getAllAlarmAndWarningCategoryDistributionBetweenDatesWithReturn(
  startDate: string,
  endDate: string,
  locationId: string = '',
  cameraId: string = ''
) {
  dispatch(slice.actions.startAlarmAndWarningCategoryDistributionLoading());
  try {
    if (startDate.includes('T')) {
      startDate = startDate.slice(0, startDate.indexOf('T'));
    }
    if (endDate.includes('T')) {
      endDate = endDate.slice(0, endDate.indexOf('T'));
    }
    let requestURL = `${CONSTS.DASHBOARD_GET_GET_FILTER_EVENTS_BY_CATEGORY_AND_DATE_MODULE_ALARM_AND_WARNING_CATEGORY_DISTRIBUTION}?startDate=${startDate}&endDate=${endDate}`;
    if (locationId) {
      requestURL += `&locationId=${locationId}`;
    }
    if (cameraId) {
      requestURL += `&cameraId=${cameraId}`;
    }
    const response: AxiosResponseModel<
      AlarmAndWarningCategoryDistributionModel[]
    > = await axios.get(requestURL).catch((error) => {
      if (error.response) {
        return error.response.data;
      }
      return error;
    });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.getAllAlarmAndWarningCategoryDistributionSuccess(
          response.data?.data
        )
      );
    } else {
      dispatch(
        slice.actions.getAllAlarmAndWarningCategoryDistributionSuccess([])
      );
    }
    return response;
  } catch (error: any) {
    dispatch(slice.actions.hasErrorAlarmAndWarningCategoryDistribution(error));
    return error.response.data;
  }
}

// ----------------------------------------------------------------------
